import { useState } from 'react'
import { createPortal } from 'react-dom'
import Img from 'src/components/shared/Img'
import Typography from 'src/components/shared/Typography'
import { paymentsReferenceTable as deafultPaymentValues } from '../SliderCTA/data'
import { numberFormat, getTimeFormat } from 'src/common/utils'
import useDeviceDetect from 'src/common/hooks/useDeviceDetect'
import Modal from 'src/components/shared/Modal'
import { newCfosDisclousures } from 'src/components/layouts/consts'
import { useNavigateToApply } from 'src/common/hooks/useNavigateToApply'
import { useAnalytics } from 'src/common/analytics'
import { ultramarine } from 'src/components/shared/styles'
import { Section } from 'src/components/shared/SectionV2'

const amountFormatOptions = {
  style: 'currency',
  currency: 'USD',
}

const DebtReliefCalculatorV2 = ({
  data = {},
  initialAmount = 25000,
  cfosData,
  isCfosDisclousuresVariationActive,
}) => {
  const { isMobile } = useDeviceDetect()
  const { title = '', title1 = '', buttonText = '', linkText = '' } = data
  const useCfosData = cfosData && isCfosDisclousuresVariationActive
  const paymentsReferenceTable = useCfosData ? cfosData : deafultPaymentValues
  const defaultValue =
    paymentsReferenceTable[initialAmount] &&
    paymentsReferenceTable[initialAmount][0]
      ? initialAmount
      : 25000
  const [selectedAmount, setSelectedAmount] = useState(defaultValue)
  const [showModal, setShowModal] = useState(false)
  const values = Object.keys(paymentsReferenceTable)

  const { track } = useAnalytics()
  const { navigateToApply, getUrl } = useNavigateToApply({
    estimated_debt: selectedAmount,
  })

  const onClickButton = (e, params = {}) => {
    e.preventDefault()
    const variationText = e.target.innerText
    const data = {
      nav_link_section: 'Debt relief calc',
      page_section: `You could free up ${numberFormat(
        paymentsReferenceTable[selectedAmount][0] -
          paymentsReferenceTable[selectedAmount][2],
        amountFormatOptions
      )} on your monthly payment`,
      click_type: 'Button Click',
      click_id: variationText,
      click_text: `FDR-Web | ${variationText}`,
      form_ss_amount: +selectedAmount,
      track_event: 'button_click',
      ...params,
    }

    track(
      data,
      {
        event_type: 'conversion',
      },
      'button_click'
    )

    navigateToApply()
  }

  const onOpenPopup = () => {
    setShowModal(true)
  }

  const onClosePopup = () => {
    setShowModal(false)
  }

  return (
    <div
      className="flex flex-col items-center bg-secondary px-5 py-12 lg:px-0 lg:py-16"
      id="data-visualization-debt-relief-calculator"
      data-eh-observe="debt-relief-calculator-v2"
      data-testid="debt-relief-calculator"
    >
      <Section.Typography
        variant="heading-large"
        as="h2"
        className="lg:w-160 mb-6 w-full lg:mb-8"
      >
        You could free up&nbsp;
        <Section.Typography
          as="span"
          variant="heading-large"
          className="text-blue-140"
        >
          {numberFormat(
            paymentsReferenceTable[selectedAmount][0] -
              paymentsReferenceTable[selectedAmount][2],
            amountFormatOptions
          )}
        </Section.Typography>
        &nbsp;on your monthly payment
      </Section.Typography>
      <div className="lg:w-160 w-full" data-testid="debt-calculator-input">
        <label className="mb-2 block w-full text-center text-body-medium font-medium leading-[120%]">
          Select your debt amount
        </label>
        <select
          style={{
            WebkitAppearance: 'none',
            backgroundImage: 'url("/next-assets/icons/chevron-down.svg")',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '24px 24px',
            backgroundPosition: 'calc(100% - 8px) center',
          }}
          id="amount"
          className="text-md block w-full rounded-lg border border-neutral-150 p-2.5 font-bold text-content-primary outline-1 outline-blue-accent"
          value={selectedAmount}
          onChange={(e) => setSelectedAmount(e.target.value)}
        >
          {values.map((val) => (
            <option
              key={`val-item-${val}`}
              className="text-blue-700"
              value={val}
            >
              {numberFormat(val, amountFormatOptions)}
            </option>
          ))}
        </select>
      </div>
      <div
        className="lg:w-160 mt-4 flex w-full flex-col items-center rounded-16 border border-blue-accent bg-white pt-5 pb-8 lg:mt-8"
        data-testid="debt-calculator-results"
      >
        <table className="w-full" data-testid="debt-calculator-results-table">
          <thead>
            <th className="w-[115px] lg:w-[350px]"></th>
            <th className="lg:w-35 w-[98px] pl-4 text-left align-middle">
              <Typography
                component="span"
                variant={isMobile ? 'labelSmall' : 'labelMedium'}
              >
                Minimum payments
              </Typography>
            </th>
            <th className="lg:w-35 w-[121px] align-middle">
              <Img
                width={124}
                height={36}
                src={
                  isMobile
                    ? '/next-assets/icons/fdr-logo-centered.svg'
                    : '/next-assets/icons/fdr-logo-left.svg'
                }
                alt="logo FDR"
                priority
                layout="intrinsic"
              />
            </th>
          </thead>
          <tbody>
            <tr
              className="border-t border-neutral-150"
              data-testid="debt-calculator-result-row"
            >
              <td className="px-2 py-4 lg:px-8 lg:py-6">
                <Typography
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                  color="secondary"
                >
                  Pay less each month <sup>1</sup>
                </Typography>
              </td>
              <td
                className="px-3 lg:px-4 lg:py-6"
                data-testid="debt-calculator-current-value"
              >
                <Typography
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                >
                  {numberFormat(
                    paymentsReferenceTable[selectedAmount][0],
                    amountFormatOptions
                  )}
                  /mo
                </Typography>
              </td>
              <td
                className="relative bg-green-40 py-6 pr-7 pl-3 lg:pr-9 lg:pl-4"
                data-testid="debt-calculator-fdr-value"
              >
                <div className="flex items-center justify-start gap-[5px] lg:block">
                  <Typography
                    component="span"
                    variant="displayXSmall"
                    color="positive"
                  >
                    {numberFormat(
                      paymentsReferenceTable[selectedAmount][2],
                      amountFormatOptions
                    )}
                    /mo
                  </Typography>
                  <span className="size-4 shrink-0 lg:absolute lg:top-1/2 lg:right-4 lg:-translate-y-1/2">
                    <Img
                      width={16}
                      height={16}
                      src="/next-assets/icons/check-green.svg"
                      alt="Check green icon"
                      priority
                      layout="intrinsic"
                      imgClassName="h-4 w-4"
                    />
                  </span>
                </div>
              </td>
            </tr>
            <tr
              className="border-t border-neutral-150"
              data-testid="debt-calculator-result-row"
            >
              <td className="px-2 py-4 lg:px-8 lg:py-6">
                <Typography
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                  color="secondary"
                >
                  Get rid of debt faster <sup>2</sup>
                </Typography>
              </td>
              <td
                className="px-3 lg:px-4 lg:py-6"
                data-testid="debt-calculator-current-value"
              >
                <Typography
                  className="whitespace-pre-wrap"
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                >
                  {useCfosData
                    ? getTimeFormat(
                        paymentsReferenceTable[selectedAmount][4],
                        isMobile,
                        { months: { one: 'mo', other: 'mo' } }
                      )
                    : `4 yrs${isMobile ? '\n' : ' '}9 mo`}
                </Typography>
              </td>
              <td
                className="relative bg-green-40 py-6 pr-7 pl-3 lg:pr-9 lg:pl-4"
                data-testid="debt-calculator-fdr-value"
              >
                <div className="flex items-center justify-start gap-[5px] lg:block">
                  <Typography
                    className="whitespace-pre-wrap"
                    component="span"
                    variant="displayXSmall"
                    color="positive"
                  >
                    {useCfosData
                      ? getTimeFormat(
                          paymentsReferenceTable[selectedAmount][5],
                          isMobile,
                          { months: { one: 'mo', other: 'mo' } }
                        )
                      : '4 years'}
                  </Typography>
                  <span className="size-4 shrink-0 lg:absolute lg:top-1/2 lg:right-4 lg:-translate-y-1/2">
                    <Img
                      width={16}
                      height={16}
                      src="/next-assets/icons/check-green.svg"
                      alt="Check green icon"
                      priority
                      layout="intrinsic"
                      imgClassName="h-4 w-4"
                    />
                  </span>
                </div>
              </td>
            </tr>
            <tr
              className="border-t border-neutral-150"
              data-testid="debt-calculator-result-row"
            >
              <td className="px-2 py-4 lg:px-8 lg:py-6">
                <Typography
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                  color="secondary"
                >
                  Lower your overall debt cost <sup>3</sup>
                </Typography>
              </td>
              <td
                className="px-3 lg:px-4 lg:py-6"
                data-testid="debt-calculator-current-value"
              >
                <Typography
                  component="span"
                  variant={isMobile ? 'labelSmall' : 'labelMedium'}
                >
                  {numberFormat(
                    paymentsReferenceTable[selectedAmount][1],
                    amountFormatOptions
                  )}
                </Typography>
              </td>
              <td
                className="relative bg-green-40 py-6 pr-7 pl-3 lg:pr-9 lg:pl-4"
                data-testid="debt-calculator-fdr-value"
              >
                <div className="flex items-center justify-start gap-[5px] lg:block">
                  <Typography
                    component="span"
                    variant="displayXSmall"
                    color="positive"
                  >
                    {numberFormat(
                      paymentsReferenceTable[selectedAmount][3],
                      amountFormatOptions
                    )}
                  </Typography>
                  <span className="size-4 shrink-0 lg:absolute lg:top-1/2 lg:right-4 lg:-translate-y-1/2">
                    <Img
                      width={16}
                      height={16}
                      src="/next-assets/icons/check-green.svg"
                      alt="Check green icon"
                      priority
                      layout="intrinsic"
                      imgClassName="h-4 w-4"
                    />
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <a
          className="w-75 lg:w-104 mt-4 block cursor-pointer rounded-md bg-blue-accent py-3 text-center text-label-button-medium font-bold text-white lg:mx-0"
          href={getUrl}
          onClick={onClickButton}
          data-testid="debt-calculator-cta"
        >
          {buttonText}
        </a>
        <a
          className="mt-6 block cursor-pointer text-center text-body-small text-content-secondary underline lg:text-body-medium"
          onClick={onOpenPopup}
          data-testid="debt-calculator-disclosure-link"
        >
          {linkText}
        </a>
        {showModal &&
          createPortal(
            <Modal
              backdropClassNames={`${ultramarine.variable} font-ultramarine`}
              bodyClassName="max-h-[90vh]"
              onClose={onClosePopup}
              open={showModal}
              className="max-w-150 lg:w-150 w-100 "
            >
              <div className="p-5">
                <Typography component="span" variant="headingMedium">
                  How are these numbers calculated?
                </Typography>
                <Typography
                  className="whitespace-pre-line"
                  component="p"
                  align="left"
                  color="secondary"
                  variant="bodyMedium"
                  mt={5}
                >
                  {newCfosDisclousures}
                </Typography>
                <div className="flex justify-end">
                  <a
                    onClick={onClosePopup}
                    className="lg:w-31 group flex w-full cursor-pointer items-center justify-center gap-x-3 rounded-lg bg-blue-accent px-6 py-3"
                  >
                    <span className="whitespace-nowrap text-lg font-bold text-white">
                      Go back
                    </span>
                  </a>
                </div>
              </div>
            </Modal>,
            document.body
          )}
      </div>
    </div>
  )
}

export default DebtReliefCalculatorV2
